import type { CSSProperties } from 'react';
import { WIDGET_GRID_COLUMNS } from '@/config/schema';

/**
 * `component.span` -> the custom property the `.widget-grid > *` rule reads.
 *
 * ## Why a custom property and not a class
 *
 * The span is config data. `grid-column: span 7` cannot be a static Tailwind utility that
 * survives to the bundle — the value lives in a JSON file on a deployed server and can be
 * edited long after the build, which is the same blind spot `@source inline(...)` exists for
 * in `src/styles/index.css`. One custom property per container and one static rule in
 * `global.css` cannot drift out of the bundle, whatever a config asks for.
 *
 * ## Why the property is always written
 *
 * `.widget-grid > *` declares `grid-column: span var(--widget-span, 12)`, so an unset property
 * already renders full width. Writing `12` explicitly anyway costs nothing at render time (a
 * custom property has no layout effect when it equals the fallback) and buys two things: the
 * span of every widget is legible in DevTools on a display nobody can attach a debugger to,
 * and it is assertable in jsdom, which computes no grid layout at all.
 */
export function widgetSpanStyle(span: number | undefined): CSSProperties {
  // The cast is the standard React escape hatch for custom properties: `CSSProperties` has
  // no index signature for them.
  return { '--widget-span': String(span ?? WIDGET_GRID_COLUMNS) } as CSSProperties;
}

/**
 * `count` widgets sharing one row of the grid, as spans that sum to exactly
 * {@link WIDGET_GRID_COLUMNS}.
 *
 * This is what `GroupWidget`'s "equal-width columns" means once there is a single layout
 * model: three children are `4 4 4`, four are `3 3 3 3`, six are `2 2 2 2 2 2`.
 *
 * **Twelve does not divide every count.** Five children are `3 3 2 2 2` — the remainder goes
 * to the leading columns rather than leaving a two-column hole at the end, so the row is still
 * full and the widest columns are the first ones a reader looks at. Exactly equal columns are
 * only possible for a count that divides 12, and paying for the other counts with a second
 * layout model (a group-local grid of `n` tracks) is a worse trade than 1/12 of a row of
 * asymmetry in a widget no deployed config uses.
 *
 * More than twelve children get `1` each and wrap onto further rows, which is grid
 * auto-placement doing the sensible thing. `count <= 0` returns an empty array.
 */
export function distributeSpans(count: number): number[] {
  if (count <= 0) return [];
  if (count >= WIDGET_GRID_COLUMNS) return Array.from({ length: count }, () => 1);

  const share = Math.floor(WIDGET_GRID_COLUMNS / count);
  const remainder = WIDGET_GRID_COLUMNS % count;
  return Array.from({ length: count }, (_, index) => share + (index < remainder ? 1 : 0));
}
